import { useState } from 'react';
import { PlanGeneralSchema, type PlanGeneralForm } from '../schemas/parameters.schema';
import type { FertilizationPlanReferenceDTO } from '../types/parameters.types';
import { toFieldErrors, type FieldErrors } from '../utils/formErrors';

interface UsePlanFormParams {
	plans: FertilizationPlanReferenceDTO[];
	plan: FertilizationPlanReferenceDTO | null;
	onSubmit: (form: PlanGeneralForm, base: FertilizationPlanReferenceDTO) => Promise<boolean>;
	onClose: () => void;
}

const nextVersion = (plans: FertilizationPlanReferenceDTO[]) => Math.max(0, ...plans.map((item) => item.version)) + 1;

export const usePlanForm = ({ plans, plan, onSubmit, onClose }: UsePlanFormParams) => {
	const activePlan = plans.find((item) => item.isActive);
	const [form, setForm] = useState<PlanGeneralForm>({
		name: plan?.name ?? '',
		version: plan?.version ?? nextVersion(plans),
		description: plan?.description ?? '',
	});
	const [baseId, setBaseId] = useState(plan?._id ?? activePlan?._id ?? plans[0]?._id ?? '');
	const [errors, setErrors] = useState<FieldErrors>({});

	const setField = <K extends keyof PlanGeneralForm>(key: K, value: PlanGeneralForm[K]) => {
		setErrors((previous) => ({ ...previous, [key]: '' }));
		setForm((previous) => ({ ...previous, [key]: value }));
	};

	const submit = async () => {
		const parsed = PlanGeneralSchema.safeParse(form);
		if (!parsed.success) {
			setErrors(toFieldErrors(parsed.error));
			return;
		}
		const base = plans.find((item) => item._id === baseId);
		if (base && (await onSubmit(parsed.data, base))) onClose();
	};

	return { form, baseId, activePlan, errors, setField, setBaseId, submit };
};
